import type {
  EventDate,
  EventDateType,
  EventDescription,
} from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'

export const useEventData = () =>
  useState<EventDescription | undefined>('eventData', () => undefined)

// handle correct redirects for events and kit pages
export const useEventRedirects = async (
  eventData: EventDescription | undefined,
  eventDataLoadError: boolean,
  isGiftcard: boolean,
  eventPermalink: string
) => {
  const $localePath = useLocalePath()
  const localePath = $localePath
  const route = useRoute()
  const isSubdomain = useIsSubdomain()

  switch (true) {
    // if event is not found, throw 404
    case !eventData || eventDataLoadError:
      throw showError({
        statusCode: 404,
        message: 'Page Not Found',
      })
    // if page is in route /e/[permalink] but it's actually a giftcard page, throw 404
    case isGiftcard !== eventGetters.isGiftcardEvent(eventData):
      throw showError({
        statusCode: 404,
        message: 'Page Not Found',
      })
    // if event has published_in.embedded=false and we're on a subdomain, throw 404
    case eventData?.published_in?.embedded === false && isSubdomain.value:
      throw showError({
        statusCode: 404,
        message: 'Page Not Found',
      })
    // if event has published_in.konfetti=false and we're not on a subdomain, throw 404
    case eventData?.published_in?.konfetti === false && !isSubdomain.value:
      throw showError({
        statusCode: 404,
        message: 'Page Not Found',
      })
    // when it should be a kit page (/k/), but we land on the event page (/e/)
    case eventGetters.isBoxEvent(eventData) && route.path.includes('/e/'):
      await navigateTo(localePath(`/k/${eventPermalink}`))
      break
    // when it should be an event page (/e/), but we land on the kit page (/k/)
    case !eventGetters.isBoxEvent(eventData) && route.path.includes('/k/'):
      await navigateTo(localePath(`/e/${eventPermalink}`))
      break
  }
}

export const useEventDatesStore = (
  id: Ref<string> | string,
  type: EventDateType
) => {
  const dates = useState<EventDate[]>(
    `eventDateData-${toValue(id)}-${type}`,
    () => []
  )

  const set = (newDates: EventDate[]) => {
    dates.value = newDates
  }

  return {
    dates,
    set,
  }
}
